import { Button } from "@shared/ui/Button";
import { useManageColumnsHub } from "../lib/useManageColumnsHub";
import styles from "../styles.module.scss";
import {
  BriefTableColumnsSection,
  CustomColumnsSection,
  PerformanceMetricsSection,
} from "./ManageColumnsHubSections";

interface ManageColumnsHubProps {
  onClose?: () => void;
}

export function ManageColumnsHub({ onClose }: ManageColumnsHubProps) {
  const hub = useManageColumnsHub(onClose);

  if (hub.isFetching) {
    return <div className={styles.loading}>Loading...</div>;
  }

  return (
    <div className={styles.hubContainer}>
      <div className={styles.hubContent}>
        <p className={styles.hubDescription}>
          Choose which columns appear in the table. Click a column name to expand
          its details, or add new columns inline.
        </p>

        <BriefTableColumnsSection
          selectedKeys={hub.visibleBriefTableColumns}
          isVisible={hub.isBriefTableColumnVisible}
          onToggle={hub.toggleBriefTableColumnVisibility}
        />

        <PerformanceMetricsSection
          fields={hub.performanceFieldOptions}
          selectedCount={hub.visiblePerformanceCount}
          isVisible={hub.isPerformanceVisible}
          onToggle={hub.togglePerformanceVisibility}
          onToggleCategory={hub.togglePerformanceCategoryVisibility}
        />

        <CustomColumnsSection
          title="Custom Column · Brand"
          infoTooltip="Columns shared with the brand. Visibility updates sync to the brand dashboard."
          fields={hub.brandFieldOptions}
          visibleCount={hub.visibleBrandCount}
          defaultOpen={false}
          isCreateOpen={hub.openCreateScope === "brand"}
          isCreateDisabled={
            hub.openCreateScope !== null && hub.openCreateScope !== "brand"
          }
          expandedFieldKey={hub.expandedFieldKey}
          isVisible={hub.isBrandVisible}
          onToggle={hub.toggleBrandVisibility}
          onToggleCreate={() => hub.openCreateForm("brand")}
          onToggleFieldView={hub.toggleFieldView}
          onCloseFieldView={hub.closeFieldView}
          fieldName={hub.fieldName}
          fieldType={hub.fieldType}
          fieldDefault={hub.fieldDefault}
          dropdownOptions={hub.dropdownOptions}
          isSubmitting={hub.isCreating}
          onFieldNameChange={hub.setFieldName}
          onFieldTypeChange={hub.setFieldType}
          onFieldDefaultChange={hub.setFieldDefault}
          onDropdownOptionsChange={hub.setDropdownOptions}
          onAddOption={hub.handleAddOption}
          onRemoveOption={hub.handleRemoveOption}
          onOptionChange={hub.handleOptionChange}
          onSubmit={hub.handleCreateSubmit}
          onCancelCreate={hub.closeCreateForm}
        />

        <CustomColumnsSection
          title="Custom Column · Internal"
          infoTooltip="Columns for internal operations only. Not shared with the brand."
          fields={hub.internalFieldOptions}
          visibleCount={hub.visibleInternalCount}
          defaultOpen
          isCreateOpen={hub.openCreateScope === "internal"}
          isCreateDisabled={
            hub.openCreateScope !== null && hub.openCreateScope !== "internal"
          }
          expandedFieldKey={hub.expandedFieldKey}
          isVisible={hub.isInternalVisible}
          onToggle={hub.toggleInternalVisibility}
          onToggleCreate={() => hub.openCreateForm("internal")}
          onToggleFieldView={hub.toggleFieldView}
          onCloseFieldView={hub.closeFieldView}
          onDelete={hub.handleDeleteField}
          isDeleting={hub.isFieldDeleting}
          fieldName={hub.fieldName}
          fieldType={hub.fieldType}
          fieldDefault={hub.fieldDefault}
          dropdownOptions={hub.dropdownOptions}
          isSubmitting={hub.isCreating}
          onFieldNameChange={hub.setFieldName}
          onFieldTypeChange={hub.setFieldType}
          onFieldDefaultChange={hub.setFieldDefault}
          onDropdownOptionsChange={hub.setDropdownOptions}
          onAddOption={hub.handleAddOption}
          onRemoveOption={hub.handleRemoveOption}
          onOptionChange={hub.handleOptionChange}
          onSubmit={hub.handleCreateSubmit}
          onCancelCreate={hub.closeCreateForm}
        />
      </div>

      <div className={styles.hubFooter}>
        <Button
          type="primary"
          block
          loading={hub.isToggling}
          onClick={hub.handleApplyChanges}
          className={styles.applyButton}
        >
          Apply Changes
        </Button>
      </div>
    </div>
  );
}
